<script setup>
import { nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import wootConstants from 'dashboard/constants/globals';

const props = defineProps({
  search: {
    type: String,
    default: '',
  },
  readFilter: {
    type: String,
    default: 'unread',
  },
  sortOrder: {
    type: String,
    default: wootConstants.INBOX_SORT_BY.NEWEST,
  },
});

const emit = defineEmits([
  'update:search',
  'update:read-filter',
  'update:sort-order',
]);

const { t } = useI18n();
const showSortMenu = ref(false);
const sortButton = ref(null);
const sortMenu = ref(null);
const sortMenuStyle = ref({});

const readTabs = [
  { key: 'unread', label: 'Não lidas' },
  { key: 'read', label: 'Lidas' },
];

const sortOptions = [
  {
    key: wootConstants.INBOX_SORT_BY.NEWEST,
    label: t('INBOX.DISPLAY_MENU.SORT_OPTIONS.NEWEST'),
    icon: 'i-lucide-arrow-down',
  },
  {
    key: wootConstants.INBOX_SORT_BY.OLDEST,
    label: t('INBOX.DISPLAY_MENU.SORT_OPTIONS.OLDEST'),
    icon: 'i-lucide-arrow-up',
  },
];

const activeSortLabel = () =>
  sortOptions.find(option => option.key === props.sortOrder)?.label ||
  sortOptions[0].label;

const updateSearch = event => {
  emit('update:search', event.target.value);
};

const clearSearch = () => {
  emit('update:search', '');
};

const updateSortMenuPosition = () => {
  const button = sortButton.value;
  if (!button) return;

  const buttonRect = button.getBoundingClientRect();
  const menuWidth = 420;
  const viewportPadding = 16;
  const preferredLeft = buttonRect.right + 8;
  const maxLeft = window.innerWidth - menuWidth - viewportPadding;
  const left = Math.max(
    viewportPadding,
    Math.min(preferredLeft, Math.max(viewportPadding, maxLeft))
  );
  const top = Math.min(
    Math.max(buttonRect.top + buttonRect.height / 2, viewportPadding),
    window.innerHeight - viewportPadding
  );

  sortMenuStyle.value = {
    position: 'fixed',
    left: `${left}px`,
    top: `${top}px`,
    transform: 'translateY(-50%)',
  };
};

const toggleSortMenu = async () => {
  if (showSortMenu.value) {
    showSortMenu.value = false;
    return;
  }

  updateSortMenuPosition();
  showSortMenu.value = true;
  await nextTick();
  updateSortMenuPosition();
};

const closeSortMenuOnOutsidePointer = event => {
  if (!showSortMenu.value) return;
  if (sortButton.value?.contains(event.target)) return;
  if (sortMenu.value?.contains(event.target)) return;
  showSortMenu.value = false;
};

const selectReadFilter = key => {
  if (key === props.readFilter) return;
  emit('update:read-filter', key);
};

const selectSort = key => {
  showSortMenu.value = false;
  if (key === props.sortOrder) return;
  emit('update:sort-order', key);
};

onMounted(() => {
  document.addEventListener('pointerdown', closeSortMenuOnOutsidePointer);
  window.addEventListener('resize', updateSortMenuPosition);
  window.addEventListener('scroll', updateSortMenuPosition, true);
});

onBeforeUnmount(() => {
  document.removeEventListener('pointerdown', closeSortMenuOnOutsidePointer);
  window.removeEventListener('resize', updateSortMenuPosition);
  window.removeEventListener('scroll', updateSortMenuPosition, true);
});
</script>

<template>
  <div class="acl-inbox-list-controls">
    <label class="acl-inbox-search-pill">
      <span class="i-lucide-search acl-inbox-search-pill__icon" />
      <input
        :value="search"
        type="search"
        autocomplete="off"
        :placeholder="t('GENERAL.PHONE_INPUT.PLACEHOLDER')"
        class="acl-inbox-search-pill__input"
        @input="updateSearch"
      />
      <button
        v-if="search"
        type="button"
        class="acl-inbox-search-pill__clear"
        :aria-label="t('GENERAL.CLOSE')"
        @click="clearSearch"
      >
        <span class="i-lucide-x" />
      </button>
    </label>

    <div class="acl-inbox-filter-row">
      <div
        class="acl-inbox-read-tabs"
        role="tablist"
        :aria-label="t('INBOX.DISPLAY_MENU.DISPLAY')"
      >
        <button
          v-for="tab in readTabs"
          :key="tab.key"
          type="button"
          role="tab"
          class="acl-inbox-read-tab"
          :class="{
            'acl-inbox-read-tab--active': readFilter === tab.key,
          }"
          :aria-selected="readFilter === tab.key"
          @click="selectReadFilter(tab.key)"
        >
          <span>{{ tab.label }}</span>
        </button>
      </div>

      <div class="acl-inbox-sort-wrap">
        <button
          ref="sortButton"
          type="button"
          class="acl-inbox-sort-button"
          :class="{ 'acl-inbox-sort-button--active': showSortMenu }"
          :aria-label="t('INBOX.DISPLAY_MENU.SORT')"
          :aria-expanded="showSortMenu"
          aria-haspopup="menu"
          @click="toggleSortMenu"
        >
          <span class="i-lucide-arrow-down-narrow-wide" aria-hidden="true" />
        </button>
      </div>
    </div>
  </div>

  <Teleport to="body">
    <div
      v-if="showSortMenu"
      ref="sortMenu"
      class="acl-inbox-sort-menu acl-inbox-sort-menu--teleported"
      :style="sortMenuStyle"
      role="menu"
      @pointerdown.stop
    >
      <button
        v-for="option in sortOptions"
        :key="option.key"
        type="button"
        role="menuitemradio"
        class="acl-inbox-sort-option"
        :class="{
          'acl-inbox-sort-option--active': sortOrder === option.key,
        }"
        :aria-checked="sortOrder === option.key"
        :title="activeSortLabel()"
        @click="selectSort(option.key)"
      >
        <span :class="option.icon" aria-hidden="true" />
        <span>{{ option.label }}</span>
        <span
          v-if="sortOrder === option.key"
          class="i-lucide-check"
          aria-hidden="true"
        />
      </button>
    </div>
  </Teleport>
</template>

<style scoped>
.acl-inbox-list-controls {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  width: 100%;
  padding: 6px 20px 10px;
  background: #fff;
  border-bottom: 0;
  backdrop-filter: blur(12px);
}

.acl-inbox-search-pill {
  position: relative;
  display: flex;
  align-items: center;
  gap: 8px;
  width: 100%;
  min-height: 40px;
  padding: 3px 12px;
  border-radius: 9999px;
  background: rgba(194, 189, 184, 0.15);
  transition: box-shadow 0.12s;
}

.acl-inbox-search-pill:hover {
  box-shadow: inset 0 0 0 1px #c4c4c4;
}

.acl-inbox-search-pill:focus-within {
  box-shadow: inset 0 0 0 1.5px #1b8755;
}

.acl-inbox-search-pill__icon {
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  color: #8a8a8a;
}

.acl-inbox-search-pill__input {
  flex: 1;
  min-width: 0;
  height: 34px;
  padding: 0;
  margin: 0;
  color: #1f2937;
  font-size: 13px;
  line-height: 34px;
  background: transparent !important;
  border: 0 !important;
  outline: 0 !important;
  box-shadow: none !important;
}

.acl-inbox-search-pill__input::placeholder {
  color: #8a8a8a;
}

.acl-inbox-search-pill__clear,
.acl-inbox-sort-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 32px;
  height: 32px;
  padding: 0;
  color: #525252;
  cursor: pointer;
  background: transparent;
  border: 0;
  border-radius: 50%;
  transition:
    background-color 0.12s,
    color 0.12s;
}

.acl-inbox-search-pill__clear:hover,
.acl-inbox-sort-button:hover {
  color: #0a0a0a;
  background: rgba(10, 10, 10, 0.06);
}

.acl-inbox-sort-button--active,
.acl-inbox-sort-button--active:hover {
  color: #15603e;
  background: rgba(21, 96, 62, 0.12);
}

.acl-inbox-search-pill__clear > span,
.acl-inbox-sort-button > span {
  width: 24px;
  height: 24px;
}

.acl-inbox-filter-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  margin-top: 6px;
  min-width: 0;
}

.acl-inbox-read-tabs {
  display: flex;
  gap: 6px;
  min-width: 0;
  overflow-x: auto;
  scrollbar-width: none;
}

.acl-inbox-read-tabs::-webkit-scrollbar {
  display: none;
}

.acl-inbox-read-tab {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  flex-shrink: 0;
  padding: 11px 12px;
  color: #767676;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.2;
  white-space: nowrap;
  cursor: pointer;
  background: transparent;
  border: 1px solid #d4d4d4;
  border-radius: 50px;
  transition:
    background-color 0.12s,
    color 0.12s,
    border-color 0.12s;
}

.acl-inbox-read-tab:hover,
.acl-inbox-read-tab--active {
  color: #15603e;
  background: #d9fdd3;
  border-color: #15603e;
}

.acl-inbox-sort-wrap {
  position: relative;
  flex-shrink: 0;
}

.acl-inbox-sort-menu {
  position: absolute;
  top: 50%;
  left: calc(100% + 8px);
  z-index: 60;
  width: 420px;
  max-width: calc(100vw - 32px);
  display: flex;
  flex-direction: column;
  gap: 12px;
  box-sizing: border-box;
  padding: 12px;
  background: #ffffff;
  border: 1px solid #e1e4e8;
  border-radius: 10px;
  box-shadow: 0 8px 24px rgba(0, 0, 0, 0.14);
  transform: translateY(-50%);
}

.acl-inbox-sort-option {
  display: flex;
  align-items: center;
  gap: 12px;
  width: 100%;
  min-height: 48px;
  padding: 12px 16px;
  color: #343a40;
  font-size: 14px;
  font-weight: 600;
  text-align: left;
  cursor: pointer;
  background: #ffffff;
  border: 1px solid #e1e4e8;
  border-radius: 16px;
  box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
  transition:
    background-color 120ms ease,
    border-color 120ms ease,
    box-shadow 120ms ease;
}

.acl-inbox-sort-option > span:first-child {
  width: 20px;
  height: 20px;
  color: #626c76;
  background-color: currentColor;
  flex-shrink: 0;
}

.acl-inbox-sort-option > span:nth-child(2) {
  flex: 1;
  white-space: nowrap;
}

.acl-inbox-sort-option:hover {
  background-color: #f8fafc;
  border-color: #d7dce2;
  box-shadow: 0 2px 6px rgba(15, 23, 42, 0.1);
}

.acl-inbox-sort-option--active {
  color: #27332b;
  background-color: #f3faf5;
  border-color: #b8d8c3;
}

.acl-inbox-sort-option > span:last-child {
  width: 18px;
  height: 18px;
  margin-left: auto;
  color: #15603e;
  background-color: currentColor;
  flex-shrink: 0;
}

:deep(.dark) .acl-inbox-list-controls {
  background: rgba(15, 23, 42, 0.72);
  border-bottom: 0;
}

@media (max-width: 767px) {
  .acl-inbox-list-controls {
    padding-inline: 12px;
  }
}

:deep(.dark) .acl-inbox-search-pill {
  background: rgba(255, 255, 255, 0.04);
}

:deep(.dark) .acl-inbox-search-pill__icon,
:deep(.dark) .acl-inbox-search-pill__input::placeholder,
:deep(.dark) .acl-inbox-search-pill__clear,
:deep(.dark) .acl-inbox-sort-button {
  color: #a1a1aa;
}

:deep(.dark) .acl-inbox-sort-button:hover {
  color: #e5e5e5;
  background: rgba(229, 229, 229, 0.08);
}

:deep(.dark) .acl-inbox-sort-button--active,
:deep(.dark) .acl-inbox-sort-button--active:hover {
  color: #86efac;
  background: rgba(21, 96, 62, 0.25);
}

:deep(.dark) .acl-inbox-search-pill__input {
  color: #e5e5e5;
}

:deep(.dark) .acl-inbox-read-tab {
  color: #a1a1aa;
  background: rgba(17, 19, 21, 0.72);
  border-color: #3f3f46;
}

:deep(.dark) .acl-inbox-read-tab:hover,
:deep(.dark) .acl-inbox-read-tab--active {
  color: #86efac;
  background: rgba(21, 96, 62, 0.25);
  border-color: #15603e;
}

:deep(.dark) .acl-inbox-sort-menu {
  background: #1f2123;
  border-color: #2c2f33;
}

:deep(.dark) .acl-inbox-sort-option {
  background: #202326;
  border-color: #34383d;
  color: #e5e5e5;
}

:deep(.dark) .acl-inbox-sort-option:hover {
  background-color: #292d31;
  border-color: #454a50;
}

:deep(.dark) .acl-inbox-sort-option--active {
  color: #f3f4f6;
  background-color: rgba(21, 96, 62, 0.28);
  border-color: #397554;
}
</style>
